import type { AgentRun, Doc, Issue, ProgressCard, Project } from "./types";

export const PROJECT_STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;
export const RECENT_OUTCOME_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type ProjectWithUpdatedAt = Project & { updatedAt: number };

export type ProjectHealthKind =
  | "completed"
  | "canceled"
  | "paused"
  | "needs_attention"
  | "stale"
  | "on_track"
  | "planned";

export interface ProjectHealth {
  kind: ProjectHealthKind;
  label: string;
  reason: string;
}

export type ProjectAttentionReasonKind =
  | "target_overdue"
  | "overdue_open"
  | "latest_card_blockers"
  | "urgent_active"
  | "requester_waiting_review";

export interface ProjectAttentionReason {
  kind: ProjectAttentionReasonKind;
  count: number;
  label: string;
  issueIds: string[];
  details: string[];
}

export interface ProjectInsightCounts {
  /** Work accepted into the project: todo + doing + review + done. */
  committed: number;
  done: number;
  /** Accepted work that is not done: todo + doing + review. */
  open: number;
  doing: number;
  review: number;
  backlog: number;
  todo: number;
  canceled: number;
  total: number;
  doneLast7d: number;
  completionPercent: number;
}

export type ProjectSignalSource =
  | "project"
  | "issue"
  | "progress_card"
  | "doc"
  | "agent_run";

export interface ProjectSignal {
  at: number;
  source: ProjectSignalSource;
  entityId: string;
}

export interface DeriveProjectInsightsInput {
  project: ProjectWithUpdatedAt;
  issues: readonly Issue[];
  progressCards?: readonly ProgressCard[];
  docs?: readonly Doc[];
  agentRuns?: readonly AgentRun[];
  /** Injected for deterministic server rendering and tests. Defaults to Date.now(). */
  now?: number;
}

export interface ProjectInsights {
  counts: ProjectInsightCounts;
  attentionCount: number;
  attentionReasons: ProjectAttentionReason[];
  health: ProjectHealth;
  latestSignalAt: number;
  latestSignal: ProjectSignal;
  latestProgressCard: ProgressCard | null;
  currentFocus: Issue[];
  review: Issue[];
  next: Issue[];
  /** Done issues whose stable completion timestamp falls in the last seven days. */
  recentOutcomes: Issue[];
  projectIssues: Issue[];
  projectDocs: Doc[];
  projectProgressCards: ProgressCard[];
  scopedAgentRuns: AgentRun[];
}

const ACTIVE_STATUSES = new Set<Issue["status"]>([
  "todo",
  "in_progress",
  "in_review",
]);
const OPEN_STATUSES = new Set<Issue["status"]>([
  "backlog",
  "todo",
  "in_progress",
  "in_review",
]);
const PRIORITY_ORDER: Record<Issue["priority"], number> = {
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  0: 4,
};

export function deriveProjectInsights({
  project,
  issues,
  progressCards = [],
  docs = [],
  agentRuns = [],
  now = Date.now(),
}: DeriveProjectInsightsInput): ProjectInsights {
  const projectIssues = issues.filter(
    (issue) =>
      issue.workspaceId === project.workspaceId && issue.projectId === project.id
  );
  const projectDocs = docs.filter(
    (doc) => doc.workspaceId === project.workspaceId && doc.projectId === project.id
  );
  const projectProgressCards = progressCards
    .filter(
      (card) =>
        card.workspaceId === project.workspaceId && card.projectId === project.id
    )
    .sort(compareProgressCardsNewestFirst);
  // Drafts are working notes, not executive signals. Keep them in the scoped
  // collection for counts/editing, but only published pulses can drive health,
  // blockers, or the visible current project pulse.
  const publishedProgressCards = projectProgressCards.filter(
    (card) => card.status === "published"
  );
  const latestProgressCard = publishedProgressCards[0] ?? null;

  const issueIds = new Set(projectIssues.map((issue) => issue.id));
  const docIds = new Set(projectDocs.map((doc) => doc.id));
  const scopedAgentRuns = agentRuns
    .filter(
      (run) =>
        run.workspaceId === project.workspaceId &&
        (run.projectIds.includes(project.id) ||
          run.touchedIssueIds.some((id) => issueIds.has(id)) ||
          run.touchedDocIds.some((id) => docIds.has(id)))
    )
    .sort(compareRunsNewestFirst);

  const recentOutcomeThreshold = now - RECENT_OUTCOME_WINDOW_MS;
  const recentOutcomes = projectIssues
    .filter(
      (issue) =>
        issue.status === "done" &&
        issue.completedAt !== null &&
        issue.completedAt >= recentOutcomeThreshold &&
        issue.completedAt <= now
    )
    .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));

  const done = countStatus(projectIssues, "done");
  const doing = countStatus(projectIssues, "in_progress");
  const reviewCount = countStatus(projectIssues, "in_review");
  const todo = countStatus(projectIssues, "todo");
  const backlog = countStatus(projectIssues, "backlog");
  const canceled = countStatus(projectIssues, "canceled");
  const committed = done + doing + reviewCount + todo;
  const open = doing + reviewCount + todo;
  const counts: ProjectInsightCounts = {
    committed,
    done,
    open,
    doing,
    review: reviewCount,
    backlog,
    todo,
    canceled,
    total: projectIssues.length,
    doneLast7d: recentOutcomes.length,
    completionPercent: committed ? Math.round((done / committed) * 100) : 0,
  };

  const currentFocus = projectIssues
    .filter((issue) => issue.status === "in_progress")
    .sort((a, b) => compareWork(a, b, now));
  const review = projectIssues
    .filter((issue) => issue.status === "in_review")
    .sort((a, b) => compareWork(a, b, now));
  const next = projectIssues
    .filter((issue) => issue.status === "todo")
    .sort((a, b) => compareWork(a, b, now));

  const attentionReasons = deriveAttentionReasons({
    project,
    projectIssues,
    latestProgressCard,
    now,
  });
  const attentionCount = attentionReasons.reduce(
    (total, reason) => total + reason.count,
    0
  );

  const latestSignal = deriveLatestSignal({
    project,
    projectIssues,
    projectProgressCards: publishedProgressCards,
    projectDocs,
    scopedAgentRuns,
  });
  const health = deriveHealth({
    project,
    counts,
    attentionCount,
    attentionReasons,
    latestSignalAt: latestSignal.at,
    now,
  });

  return {
    counts,
    attentionCount,
    attentionReasons,
    health,
    latestSignalAt: latestSignal.at,
    latestSignal,
    latestProgressCard,
    currentFocus,
    review,
    next,
    recentOutcomes,
    projectIssues,
    projectDocs,
    projectProgressCards,
    scopedAgentRuns,
  };
}

function deriveAttentionReasons({
  project,
  projectIssues,
  latestProgressCard,
  now,
}: {
  project: ProjectWithUpdatedAt;
  projectIssues: Issue[];
  latestProgressCard: ProgressCard | null;
  now: number;
}): ProjectAttentionReason[] {
  const reasons: ProjectAttentionReason[] = [];
  const targetOverdue =
    project.status !== "completed" &&
    project.status !== "canceled" &&
    project.targetDate !== null &&
    isBeforeToday(project.targetDate, now);

  if (targetOverdue) {
    reasons.push({
      kind: "target_overdue",
      count: 1,
      label: `Target date ${isoDate(project.targetDate!)} is overdue`,
      issueIds: [],
      details: [isoDate(project.targetDate!)],
    });
  }

  const overdueOpen = projectIssues.filter(
    (issue) =>
      OPEN_STATUSES.has(issue.status) &&
      issue.dueDate !== null &&
      isBeforeToday(issue.dueDate, now)
  );
  if (overdueOpen.length) {
    reasons.push({
      kind: "overdue_open",
      count: overdueOpen.length,
      label: pluralize(overdueOpen.length, "overdue open task"),
      issueIds: overdueOpen.map((issue) => issue.id),
      details: overdueOpen.map((issue) => issue.identifier),
    });
  }

  const blockers = (latestProgressCard?.blockers ?? [])
    .map((blocker) => blocker.trim())
    .filter(Boolean);
  if (blockers.length) {
    reasons.push({
      kind: "latest_card_blockers",
      count: blockers.length,
      label: pluralize(blockers.length, "blocker in the latest progress card"),
      issueIds: [],
      details: blockers,
    });
  }

  const urgentActive = projectIssues.filter(
    (issue) => issue.priority === 1 && ACTIVE_STATUSES.has(issue.status)
  );
  if (urgentActive.length) {
    reasons.push({
      kind: "urgent_active",
      count: urgentActive.length,
      label: pluralize(urgentActive.length, "urgent active task"),
      issueIds: urgentActive.map((issue) => issue.id),
      details: urgentActive.map((issue) => issue.identifier),
    });
  }

  const requesterWaitingReview = projectIssues.filter(
    (issue) => issue.status === "in_review" && issue.requesterId !== null
  );
  if (requesterWaitingReview.length) {
    reasons.push({
      kind: "requester_waiting_review",
      count: requesterWaitingReview.length,
      label: pluralize(
        requesterWaitingReview.length,
        "review waiting on its requester",
        "reviews waiting on their requesters"
      ),
      issueIds: requesterWaitingReview.map((issue) => issue.id),
      details: requesterWaitingReview.map((issue) => issue.identifier),
    });
  }

  return reasons;
}

function deriveLatestSignal({
  project,
  projectIssues,
  projectProgressCards,
  projectDocs,
  scopedAgentRuns,
}: {
  project: ProjectWithUpdatedAt;
  projectIssues: Issue[];
  projectProgressCards: ProgressCard[];
  projectDocs: Doc[];
  scopedAgentRuns: AgentRun[];
}): ProjectSignal {
  const candidates: ProjectSignal[] = [
    {
      at: finiteTimestamp(project.updatedAt, project.createdAt),
      source: "project",
      entityId: project.id,
    },
    ...projectIssues.map((issue) => ({
      at: issue.updatedAt,
      source: "issue" as const,
      entityId: issue.id,
    })),
    ...projectProgressCards.map((card) => ({
      at: card.updatedAt,
      source: "progress_card" as const,
      entityId: card.id,
    })),
    ...projectDocs.map((doc) => ({
      at: doc.updatedAt,
      source: "doc" as const,
      entityId: doc.id,
    })),
    ...scopedAgentRuns.map((run) => ({
      at: run.endedAt ?? run.startedAt ?? run.createdAt,
      source: "agent_run" as const,
      entityId: run.id,
    })),
  ];

  const validCandidates = candidates.filter((signal) => Number.isFinite(signal.at));

  validCandidates.sort(
    (a, b) =>
      b.at - a.at ||
      a.source.localeCompare(b.source) ||
      a.entityId.localeCompare(b.entityId)
  );
  return validCandidates[0] ?? {
    at: project.createdAt,
    source: "project",
    entityId: project.id,
  };
}

function deriveHealth({
  project,
  counts,
  attentionCount,
  attentionReasons,
  latestSignalAt,
  now,
}: {
  project: ProjectWithUpdatedAt;
  counts: ProjectInsightCounts;
  attentionCount: number;
  attentionReasons: ProjectAttentionReason[];
  latestSignalAt: number;
  now: number;
}): ProjectHealth {
  if (project.status === "completed") {
    return {
      kind: "completed",
      label: "Completed",
      reason: "Project is marked completed.",
    };
  }
  if (project.status === "canceled") {
    return {
      kind: "canceled",
      label: "Canceled",
      reason: "Project is marked canceled.",
    };
  }
  if (project.status === "paused") {
    return {
      kind: "paused",
      label: "Paused",
      reason: "Project is paused.",
    };
  }
  if (attentionCount > 0) {
    return {
      kind: "needs_attention",
      label: "Needs attention",
      reason: `${pluralize(attentionCount, "attention signal")}: ${attentionReasons
        .map((reason) => reason.label)
        .join("; ")}.`,
    };
  }
  if (
    project.status === "in_progress" &&
    now - latestSignalAt > PROJECT_STALE_AFTER_MS
  ) {
    return {
      kind: "stale",
      label: "Stale",
      reason: "No relevant project signal for more than 3 days while work is in progress.",
    };
  }
  if (project.status === "in_progress") {
    return {
      kind: "on_track",
      label: "On track",
      reason:
        counts.doing || counts.review
          ? `${pluralize(counts.doing + counts.review, "task")} actively moving with no attention signals.`
          : "Project is in progress with a recent signal and no attention flags.",
    };
  }
  return {
    kind: "planned",
    label: "Planned",
    reason:
      project.status === "backlog"
        ? "Project is in the backlog."
        : "Project is planned and has not started.",
  };
}

function compareWork(a: Issue, b: Issue, now: number): number {
  const aOverdue = a.dueDate !== null && isBeforeToday(a.dueDate, now);
  const bOverdue = b.dueDate !== null && isBeforeToday(b.dueDate, now);
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  const priority = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
  if (priority) return priority;
  if (a.dueDate !== b.dueDate) {
    if (a.dueDate === null) return 1;
    if (b.dueDate === null) return -1;
    return a.dueDate - b.dueDate;
  }
  return b.updatedAt - a.updatedAt || a.identifier.localeCompare(b.identifier);
}

function compareUpdatedNewestFirst(a: Issue, b: Issue): number {
  return b.updatedAt - a.updatedAt || a.identifier.localeCompare(b.identifier);
}

function compareProgressCardsNewestFirst(
  a: ProgressCard,
  b: ProgressCard
): number {
  return (
    b.periodEnd - a.periodEnd ||
    b.updatedAt - a.updatedAt ||
    b.createdAt - a.createdAt ||
    a.id.localeCompare(b.id)
  );
}

function compareRunsNewestFirst(a: AgentRun, b: AgentRun): number {
  const aAt = a.endedAt ?? a.startedAt ?? a.createdAt;
  const bAt = b.endedAt ?? b.startedAt ?? b.createdAt;
  return bAt - aAt || a.id.localeCompare(b.id);
}

function countStatus(issues: readonly Issue[], status: Issue["status"]): number {
  return issues.filter((issue) => issue.status === status).length;
}

function isBeforeToday(timestamp: number, now: number): boolean {
  return timestamp < startOfUtcDay(now);
}

function startOfUtcDay(timestamp: number): number {
  const date = new Date(timestamp);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function isoDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function finiteTimestamp(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
